import { useState, useEffect } from 'react';
import { Briefcase, Plus, Pencil, Trash2, Loader2, Users, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';

export default function AdminDesignations() {
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getDesignationsManage();
      setDesignations(res.data.designations || []);
    } catch { toast.error('Failed to load designations'); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!newName.trim()) { toast.error('Enter a designation name'); return; }
    setCreating(true);
    try {
      await adminAPI.createDesignation(newName.trim());
      toast.success(`"${newName.trim()}" created`);
      setNewName('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
    finally { setCreating(false); }
  };

  const handleRename = async (id) => {
    if (!editName.trim()) { toast.error('Name cannot be empty'); return; }
    try {
      await adminAPI.updateDesignation(id, editName.trim());
      toast.success('Designation renamed');
      setEditingId(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to rename'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await adminAPI.deleteDesignation(id);
      toast.success(`"${name}" deleted`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  return (
    <AdminLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 data-testid="designations-title" className="text-2xl font-extrabold text-[#1a2529]">Designations</h1>
            <p className="text-sm text-[#1a2529]/50 mt-0.5">Manage employee designations</p>
          </div>
          <Badge className="bg-[#026536]/10 text-[#026536] border-0">{designations.length}</Badge>
        </div>

        {/* Add New */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-border/30 flex gap-3">
          <Input
            data-testid="new-designation-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            placeholder="e.g. Senior Health Worker"
            className="h-10 flex-1"
          />
          <Button
            data-testid="create-designation-btn"
            onClick={handleCreate}
            disabled={creating || !newName.trim()}
            className="bg-[#026536] hover:bg-[#026536]/90 h-10 gap-1.5 font-semibold shrink-0"
          >
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Add
          </Button>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#026536]" size={24} /></div>
        ) : designations.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center shadow-sm border border-border/30">
            <Briefcase className="mx-auto text-[#1a2529]/15 mb-3" size={40} />
            <p className="font-semibold text-[#1a2529]">No designations yet</p>
            <p className="text-xs text-[#1a2529]/50 mt-1">Add your first designation above</p>
          </div>
        ) : (
          <div className="space-y-2">
            {designations.map((d) => (
              <div key={d.id} data-testid={`designation-${d.id}`} className="bg-white rounded-xl px-4 py-3 shadow-sm border border-border/30 flex items-center justify-between gap-3">
                {editingId === d.id ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input
                      data-testid={`edit-designation-input-${d.id}`}
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleRename(d.id)}
                      className="h-9 flex-1"
                      autoFocus
                    />
                    <button onClick={() => handleRename(d.id)} className="text-[#026536] hover:bg-[#026536]/10 rounded-lg p-1.5">
                      <Check size={16} />
                    </button>
                    <button onClick={() => setEditingId(null)} className="text-[#1a2529]/40 hover:bg-[#f4f4f5] rounded-lg p-1.5">
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="h-8 w-8 rounded-lg bg-[#026536]/5 flex items-center justify-center shrink-0">
                        <Briefcase size={14} className="text-[#026536]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#1a2529] truncate">{d.name}</p>
                        <p className="text-[10px] text-[#1a2529]/30 flex items-center gap-1">
                          <Users size={10} /> {d.employee_count} employee{d.employee_count !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        data-testid={`edit-designation-${d.id}`}
                        onClick={() => { setEditingId(d.id); setEditName(d.name); }}
                        className="text-blue-600 hover:bg-blue-50 rounded-lg p-2"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        data-testid={`delete-designation-${d.id}`}
                        onClick={() => handleDelete(d.id, d.name)}
                        className="text-red-500 hover:bg-red-50 rounded-lg p-2"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
